/**
 * CollabOpenRequestsInbox – lister åpne samarbeid-forespørsler.
 * Vises som egen tab i /skaper/samarbeid?tab=open-inbox
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import api from "../../lib/api";

const STATUS_LABEL = {
  pending: "Venter",
  accepted: "Akseptert",
  declined: "Avslått",
  cancelled: "Avlyst",
  expired: "Utløpt",
};
const STATUS_COLOR = {
  pending: "var(--gold)",
  accepted: "var(--gold)",
  declined: "var(--magenta)",
  cancelled: "var(--cream-faint)",
  expired: "var(--cream-faint)",
};

export default function CollabOpenRequestsInbox() {
  const [dir, setDir] = useState("received");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/collab/open-requests?direction=${dir}`);
        if (!cancel) setItems(r.data?.items || []);
      } catch (e) {
        if (!cancel) { toast.error(e?.response?.data?.detail || "Kunne ikke laste."); setItems([]); }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [dir]);

  return (
    <div data-testid="open-inbox">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setDir("received")}
          className={`px-3 py-1.5 font-meta text-xs border inline-flex items-center gap-1.5 ${
            dir === "received"
              ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
              : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
          }`} data-testid="open-inbox-received">
          <ArrowDownLeft className="h-3 w-3" /> Mottatt
        </button>
        <button onClick={() => setDir("sent")}
          className={`px-3 py-1.5 font-meta text-xs border inline-flex items-center gap-1.5 ${
            dir === "sent"
              ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
              : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
          }`} data-testid="open-inbox-sent">
          <ArrowUpRight className="h-3 w-3" /> Sendt
        </button>
      </div>

      {loading ? (
        <div className="panel-tight p-12 text-center text-[var(--cream-soft)] font-fraunces italic">Laster…</div>
      ) : !items || items.length === 0 ? (
        <div className="panel-tight p-12 text-center" data-testid="open-inbox-empty">
          <Inbox className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
          <div className="font-fraunces italic text-lg text-[var(--cream-soft)]">
            {dir === "received" ? "Ingen mottatte forespørsler enda." : "Du har ikke sendt noen forespørsler."}
          </div>
          {dir === "sent" && (
            <p className="text-xs text-[var(--cream-faint)] mt-2">
              Åpne en skaperprofil og trykk «Foreslå samarbeid» for å sende én.
            </p>
          )}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="open-inbox-list">
          {items.map((r) => {
            const other = dir === "received" ? r.requester_username : r.target_username;
            const typeLbl = r.type === "other" ? (r.type_other || "ANNET") : r.type.toUpperCase();
            const dateBit = r.status === "accepted" && r.accepted_date
              ? new Date(r.accepted_date).toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" })
              : r.proposed_dates.length === 0
                ? "Sjekk kalender"
                : r.proposed_dates.length === 1
                  ? new Date(r.proposed_dates[0]).toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" })
                  : `${r.proposed_dates.length} forslag`;
            return (
              <li key={r.id} className="panel-tight p-4"
                data-testid={`open-inbox-item-${r.id}`}>
                <Link to={`/skaper/samarbeid/apen/${r.id}`} className="block group">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-fraunces italic text-lg text-[var(--cream)] group-hover:text-[var(--magenta)]">
                          @{other}
                        </span>
                        <span className="font-meta text-[10px] px-2 py-0.5 border border-[var(--magenta)]/60 text-[var(--magenta)] uppercase tracking-wider">
                          {typeLbl}
                        </span>
                        <span className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)]">
                          {r.platform.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--cream-soft)] mt-1">{dateBit}</div>
                      <p className="text-sm text-[var(--cream-soft)] mt-2 line-clamp-2">{r.message}</p>
                    </div>
                    <span className="shrink-0 font-meta text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border"
                      style={{ borderColor: STATUS_COLOR[r.status], color: STATUS_COLOR[r.status] }}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
